import { supabase } from "../supabaseClient";

export const MECHANIC_BRANDS = ["hero", "bajaj", "honda", "tvs", "royal enfield", "all"];
export const MECHANIC_SERVICES = ["general service", "engine repair", "tyre", "electricals", "body work"];

const BASE_SELECT = `
  id,
  name,
  shop_name,
  city,
  village,
  district,
  state,
  phone_whatsapp,
  specialization,
  services,
  open_time,
  close_time,
  is_open_sunday,
  lat,
  lng,
  owner_user_id,
  submitted_by,
  verification_method,
  verification_notes,
  is_verified,
  created_at,
  updated_at
`;

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizePhone(value) {
  return String(value || "")
    .replace(/[^\d+]/g, "")
    .replace(/^00/, "+");
}

function normalizeSelections(values, allowed) {
  const list = Array.isArray(values) ? values : [];
  const normalized = list.map(normalizeText).filter(Boolean);
  return normalized.filter((item) => allowed.includes(item));
}

function computeRatingStats(reviews = []) {
  const ratings = reviews
    .map((review) => Number(review.rating))
    .filter((rating) => Number.isFinite(rating) && rating >= 1 && rating <= 5);

  const reviewCount = ratings.length;
  const avgRating = reviewCount
    ? Number((ratings.reduce((sum, rating) => sum + rating, 0) / reviewCount).toFixed(1))
    : 0;

  return { avgRating, reviewCount };
}

function attachRatingStats(mechanics = [], reviews = []) {
  const reviewMap = reviews.reduce((acc, review) => {
    if (!review?.mechanic_id) return acc;
    if (!acc.has(review.mechanic_id)) acc.set(review.mechanic_id, []);
    acc.get(review.mechanic_id).push(review);
    return acc;
  }, new Map());

  return mechanics.map((mechanic) => {
    const mechanicReviews = reviewMap.get(mechanic.id) || [];
    const stats = computeRatingStats(mechanicReviews);
    return {
      ...mechanic,
      reviews: mechanicReviews.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
      avg_rating: stats.avgRating,
      review_count: stats.reviewCount,
    };
  });
}

async function requireAuthenticatedUser() {
  const { data, error } = await supabase.auth.getUser();
  if (error) return { user: null, error };
  if (!data?.user) return { user: null, error: new Error("Login required") };
  return { user: data.user, error: null };
}

export async function getMechanicsByCity(city, filters = {}) {
  console.log("🔍 Search params - city:", city, "filters:", filters);

  // Fetch all verified mechanics
  const { data, error } = await supabase
    .from("mechanics")
    .select(BASE_SELECT)
    .eq("is_verified", true)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("❌ Database query error:", error);
    return { data: [], error };
  }

  console.log("✅ Fetched verified mechanics from DB:", data?.length, "records");

  // Client-side filtering for location and services
  let filtered = (data || []);

  // Filter by city/village/district/state
  const term = normalizeText(city);
  if (term) {
    filtered = filtered.filter((mechanic) => {
      const mechanicCity = normalizeText(mechanic.city);
      const mechanicVillage = normalizeText(mechanic.village);
      const mechanicDistrict = normalizeText(mechanic.district);
      const mechanicState = normalizeText(mechanic.state);

      const matches =
        mechanicCity.includes(term) ||
        mechanicVillage.includes(term) ||
        mechanicDistrict.includes(term) ||
        mechanicState.includes(term);

      if (matches) {
        console.log(`✅ Match found: ${mechanic.name} in ${mechanic.city}/${mechanic.village}`);
      }

      return matches;
    });
    console.log(`📍 Location filtering: found ${filtered.length} matches for "${city}"`);
  }

  // Filter by service if specified (but not "all")
  const service = normalizeText(filters.service);
  if (service && service !== "all") {
    filtered = filtered.filter((mechanic) => {
      const services = (mechanic.services || []).map(normalizeText);
      return services.includes(service);
    });
    console.log(`🔧 Service filtering: found ${filtered.length} matches for "${filters.service}"`);
  } else if (service === "all") {
    console.log(`🔧 Service filtering: skipped (showing all services)`);
  }

  // Filter by brand (specialization) - "all" means show all brands
  const brand = normalizeText(filters.brand);
  const mechanics = filtered.filter((mechanic) => {
    if (!brand || brand === "all") return true;
    const specializations = Array.isArray(mechanic.specialization)
      ? mechanic.specialization.map(normalizeText)
      : [];
    return specializations.includes(brand) || specializations.includes("all");
  });

  console.log(`🏍️ Brand filtering: input="${filters.brand}" normalized="${brand}" found ${mechanics.length} matches`);
  if (!mechanics.length) {
    console.log("⚠️ No mechanics match all filters");
    return { data: [], error: null };
  }

  const mechanicIds = mechanics.map((mechanic) => mechanic.id);
  const { data: reviews, error: reviewError } = await supabase
    .from("mechanic_reviews")
    .select("id, mechanic_id, user_id, rating, review_text, created_at")
    .in("mechanic_id", mechanicIds)
    .order("created_at", { ascending: false });

  if (reviewError) {
    console.log("⚠️ Review fetch error:", reviewError);
    return { data: attachRatingStats(mechanics, []), error: reviewError };
  }

  const result = attachRatingStats(mechanics, reviews || []);
  console.log("✅ Final result: returning", result.length, "mechanics with reviews");
  return { data: result, error: null };
}

export async function submitMechanic(data = {}) {
  const { user, error: authError } = await requireAuthenticatedUser();
  if (authError || !user) {
    return { data: null, error: authError || new Error("Login required") };
  }

  const payload = {
    name: String(data.name || "").trim(),
    shop_name: String(data.shop_name || "").trim(),
    city: String(data.city || "").trim(),
    village: String(data.village || "").trim(),
    district: String(data.district || "").trim(),
    state: String(data.state || "").trim(),
    phone_whatsapp: normalizePhone(data.phone_whatsapp),
    specialization: normalizeSelections(data.specialization, MECHANIC_BRANDS),
    services: normalizeSelections(data.services, MECHANIC_SERVICES),
    open_time: data.open_time || null,
    close_time: data.close_time || null,
    is_open_sunday: Boolean(data.is_open_sunday),
    lat: data.lat || null,
    lng: data.lng || null,
    owner_user_id: data.owner_user_id || user.id,
    submitted_by: user.id,
    verification_method: data.verification_method || "manual",
    verification_notes: String(data.verification_notes || "").trim() || null,
    is_verified: false,
  };

  const required = ["name", "shop_name", "city", "village", "district", "state", "phone_whatsapp"];
  for (const field of required) {
    if (!payload[field]) {
      return { data: null, error: new Error(`Missing required field: ${field}`) };
    }
  }

  if (!payload.specialization.length) {
    return { data: null, error: new Error("Select at least one specialization") };
  }

  if (!payload.services.length) {
    return { data: null, error: new Error("Select at least one service") };
  }

  const { data: inserted, error } = await supabase
    .from("mechanics")
    .insert([payload])
    .select(BASE_SELECT)
    .single();

  return { data: inserted, error };
}

export async function getMyMechanics() {
  const { user, error: authError } = await requireAuthenticatedUser();
  if (authError || !user) {
    return { data: [], error: authError || new Error("Login required") };
  }

  const { data, error } = await supabase
    .from("mechanics")
    .select(BASE_SELECT)
    .or(`owner_user_id.eq.${user.id},submitted_by.eq.${user.id}`)
    .order("created_at", { ascending: false });

  return { data: data || [], error };
}

export async function updateMechanic(mechanicId, updates = {}) {
  const { user, error: authError } = await requireAuthenticatedUser();
  if (authError || !user) {
    return { data: null, error: authError || new Error("Login required") };
  }

  const payload = {
    name: String(updates.name || "").trim(),
    shop_name: String(updates.shop_name || "").trim(),
    city: String(updates.city || "").trim(),
    village: String(updates.village || "").trim(),
    district: String(updates.district || "").trim(),
    state: String(updates.state || "").trim(),
    phone_whatsapp: normalizePhone(updates.phone_whatsapp),
    specialization: normalizeSelections(updates.specialization, MECHANIC_BRANDS),
    services: normalizeSelections(updates.services, MECHANIC_SERVICES),
    open_time: updates.open_time || null,
    close_time: updates.close_time || null,
    is_open_sunday: Boolean(updates.is_open_sunday),
    lat: updates.lat || null,
    lng: updates.lng || null,
    verification_method: updates.verification_method || "manual",
    verification_notes: String(updates.verification_notes || "").trim() || null,
  };

  const required = ["name", "shop_name", "city", "village", "district", "state", "phone_whatsapp"];
  for (const field of required) {
    if (!payload[field]) {
      return { data: null, error: new Error(`Missing required field: ${field}`) };
    }
  }

  if (!payload.specialization.length) {
    return { data: null, error: new Error("Select at least one specialization") };
  }

  if (!payload.services.length) {
    return { data: null, error: new Error("Select at least one service") };
  }

  const { data: existing, error: existingError } = await supabase
    .from("mechanics")
    .select("id, owner_user_id, submitted_by")
    .eq("id", mechanicId)
    .single();

  if (existingError) {
    return { data: null, error: existingError };
  }

  if (existing?.owner_user_id !== user.id && existing?.submitted_by !== user.id) {
    return { data: null, error: new Error("You can only edit your own garage") };
  }

  const { data, error } = await supabase
    .from("mechanics")
    .update(payload)
    .eq("id", mechanicId)
    .select(BASE_SELECT)
    .single();

  return { data, error };
}

export async function submitReview(mechanicId, rating, reviewText) {
  const { user, error: authError } = await requireAuthenticatedUser();
  if (authError || !user) {
    return { data: null, error: authError || new Error("Login required") };
  }

  const ratingValue = Number(rating);
  const text = String(reviewText || "").trim();

  if (!mechanicId) return { data: null, error: new Error("Mechanic ID is required") };
  if (!Number.isInteger(ratingValue) || ratingValue < 1 || ratingValue > 5) {
    return { data: null, error: new Error("Rating must be between 1 and 5") };
  }
  if (!text) return { data: null, error: new Error("Review text is required") };

  const { data, error } = await supabase
    .from("mechanic_reviews")
    .upsert(
      [
        {
          mechanic_id: mechanicId,
          user_id: user.id,
          rating: ratingValue,
          review_text: text,
        },
      ],
      { onConflict: "mechanic_id,user_id" }
    )
    .select("id, mechanic_id, user_id, rating, review_text, created_at")
    .single();

  return { data, error };
}

export async function getMechanicWithReviews(mechanicId) {
  const { data: mechanic, error } = await supabase
    .from("mechanics")
    .select(BASE_SELECT)
    .eq("id", mechanicId)
    .eq("is_verified", true)
    .single();

  if (error) return { data: null, error };

  const { data: reviews, error: reviewError } = await supabase
    .from("mechanic_reviews")
    .select("id, mechanic_id, user_id, rating, review_text, created_at")
    .eq("mechanic_id", mechanicId)
    .order("created_at", { ascending: false });

  if (reviewError) {
    const stats = computeRatingStats([]);
    return {
      data: {
        ...mechanic,
        reviews: [],
        avg_rating: stats.avgRating,
        review_count: stats.reviewCount,
      },
      error: reviewError,
    };
  }

  const stats = computeRatingStats(reviews || []);
  return {
    data: {
      ...mechanic,
      reviews: reviews || [],
      avg_rating: stats.avgRating,
      review_count: stats.reviewCount,
    },
    error: null,
  };
}

export async function getAverageRating(mechanicId) {
  const { data, error } = await supabase
    .from("mechanic_reviews")
    .select("rating")
    .eq("mechanic_id", mechanicId);

  if (error) {
    return { data: { avg_rating: 0, review_count: 0 }, error };
  }

  const stats = computeRatingStats(data || []);
  return { data: { avg_rating: stats.avgRating, review_count: stats.reviewCount }, error: null };
}

export function toWhatsAppLink(phone) {
  const normalized = normalizePhone(phone).replace(/\+/g, "");
  return normalized ? `https://wa.me/${normalized}` : "";
}
